package com.sddamico.draftmachine.ds.motion

import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import kotlinx.browser.document

private const val STYLE_ID = "dm-motion"

/**
 * Registers the motion stylesheet: the keyframes every component animates with, and the
 * `::view-transition-*` choreography for each [Motion]. Built from [DmMotion] so durations and
 * curves have one source. Called once from `initKobweb`, before the first page renders.
 */
fun installMotionStyles() {
  if (document.getElementById(STYLE_ID) != null) return
  val style = document.createElement("style")
  style.id = STYLE_ID
  style.textContent = motionCss()
  document.head!!.appendChild(style)
}

private fun motionCss(): String {
  val snap = DmMotion.easeSnap
  val exit = DmMotion.easeExit
  val out = DmMotion.easeOut
  val spring = DmMotion.easeSpring
  val enter = "${DmMotion.pageEnterMs}ms"
  val leave = "${DmMotion.pageExitMs}ms"
  val sheetIn = "${DmMotion.sheetEnterMs}ms"
  val sheetOut = "${DmMotion.sheetExitMs}ms"
  val vt = "html[data-dm-vt]"
  val phaseNew = "[data-dm-vt-phase]"
  val notUpdate = ":not([data-dm-vt=update])"
  fun kind(k: Motion) = "html[data-dm-vt=${k.attr}]"
  val shake = "0%, 100% { transform: translateX(0); } 18% { transform: translateX(-7px); } " +
    "36% { transform: translateX(6px); } 54% { transform: translateX(-4px); } 72% { transform: translateX(2px); }"

  return """
    @keyframes dm-fade-in { from { opacity: 0; } }
    @keyframes dm-fade-out { to { opacity: 0; } }
    @keyframes dm-rise-in { from { opacity: 0; transform: translateY(10px); } }
    @keyframes dm-push-out { to { opacity: 0; transform: translateX(-28px); } }
    @keyframes dm-push-in { from { opacity: 0; transform: translateX(44px); } }
    @keyframes dm-pop-out { to { opacity: 0; transform: translateX(28px); } }
    @keyframes dm-pop-in { from { opacity: 0; transform: translateX(-36px); } }
    @keyframes dm-recede { to { transform: scale(.94); filter: brightness(.55); } }
    @keyframes dm-emerge { from { transform: scale(.94); filter: brightness(.55); } }
    @keyframes dm-sheet-in { from { transform: translateY(100vh); } }
    @keyframes dm-sheet-out { to { transform: translateY(100vh); } }
    @keyframes dm-settle-out { to { opacity: 0; transform: scale(.985); } }
    @keyframes dm-settle-in { from { opacity: 0; transform: scale(.985); } }
    @keyframes dm-item-in { from { opacity: 0; transform: translateY(-8px) scale(.98); } }
    @keyframes dm-item-out { to { opacity: 0; transform: scale(.97); } }
    @keyframes dm-ticker-out { to { opacity: 0; transform: translateY(-16px); } }
    @keyframes dm-ticker-in { from { opacity: 0; transform: translateY(20px); } }
    @keyframes dm-stamp-in { from { opacity: 0; transform: scale(.7); } }
    @keyframes dm-dialog-in { from { opacity: 0; transform: translateY(12px) scale(.96); } }
    @keyframes dm-dialog-out { to { opacity: 0; transform: translateY(6px) scale(.97); } }
    @keyframes dm-tray-in { from { transform: translateY(100%); } }
    @keyframes dm-tray-out { to { transform: translateY(100%); } }
    @keyframes dm-menu-in { from { opacity: 0; transform: translateY(-4px) scale(.96); } }
    @keyframes dm-toast-in { from { opacity: 0; transform: translateY(18px) scale(.94); } }
    @keyframes dm-toast-out { to { opacity: 0; transform: translateY(10px) scale(.97); } }
    @keyframes dm-shake { $shake }
    @keyframes dm-shake-b { $shake }

    .dm-loading-reveal { animation: dm-fade-in 260ms $out 180ms both; }

    /* Participants. Names are only set while a transition runs, so nothing is lifted into its own
       stacking context the rest of the time. */
    $vt$notUpdate .dm-vt-page { view-transition-name: dm-page-out; }
    $vt$phaseNew$notUpdate .dm-vt-page { view-transition-name: dm-page-in; }
    ${kind(Motion.Update)} .dm-vt-page { view-transition-name: dm-page; }
    html[data-dm-vt-page-morph] .dm-vt-page { view-transition-name: ${ViewTransitions.MORPH_NAME} !important; }
    $vt .dm-vt-header { view-transition-name: dm-header; }
    $vt .dm-vt-underline { view-transition-name: dm-nav-underline; }
    ${kind(Motion.Present)} .dm-vt-header, ${kind(Motion.Dismiss)} .dm-vt-header,
    ${kind(Motion.Present)} .dm-vt-underline, ${kind(Motion.Dismiss)} .dm-vt-underline { view-transition-name: none; }
    ${kind(Motion.Forward)} .dm-vt-stepper, ${kind(Motion.Back)} .dm-vt-stepper { view-transition-name: dm-stepper; }
    ${kind(Motion.Update)} .dm-vt-item { view-transition-name: match-element; view-transition-class: dm-item; }
    ${kind(Motion.Update)} .dm-vt-segment { view-transition-name: dm-segment; }

    ::view-transition-group(*) { animation-duration: $enter; animation-timing-function: $snap; }
    ::view-transition-old(root), ::view-transition-new(root) { animation: none; }
    ::view-transition-group(${ViewTransitions.MORPH_NAME}) { z-index: 1; }
    ::view-transition-group(dm-header) { z-index: 2; animation-duration: 220ms; }
    ::view-transition-old(dm-header) { animation: 120ms $exit both dm-fade-out; }
    ::view-transition-new(dm-header) { animation: 200ms $out 60ms both dm-fade-in; }
    ::view-transition-group(dm-nav-underline) { z-index: 3; }
    ::view-transition-old(dm-nav-underline):only-child { animation: 120ms $exit both dm-fade-out; }
    ::view-transition-new(dm-nav-underline):only-child { animation: 200ms $out 60ms both dm-fade-in; }

    /* Forward / Back: the outgoing screen steps aside quickly, the incoming one arrives with weight.
       Separate out/in names keep the two snapshots in their own groups, so a scrolled page leaves
       from where it was instead of sliding to the top. */
    ${kind(Motion.Forward)}::view-transition-old(dm-page-out),
    ${kind(Motion.Forward)}:not([data-dm-vt-morphed])::view-transition-old(${ViewTransitions.MORPH_NAME}) {
      animation: $leave $exit both dm-push-out;
    }
    ${kind(Motion.Forward)}::view-transition-new(dm-page-in) { animation: $enter $snap 70ms both dm-push-in; }
    ${kind(Motion.Back)}::view-transition-old(dm-page-out),
    ${kind(Motion.Back)}:not([data-dm-vt-morphed])::view-transition-old(${ViewTransitions.MORPH_NAME}) {
      animation: $leave $exit both dm-pop-out;
    }
    ${kind(Motion.Back)}::view-transition-new(dm-page-in) { animation: $enter $snap 70ms both dm-pop-in; }

    /* Fade: siblings and redirects. No direction to express, so only a quick hand-off. */
    ${kind(Motion.Fade)}::view-transition-old(dm-page-out) { animation: 110ms $exit both dm-fade-out; }
    ${kind(Motion.Fade)}::view-transition-new(dm-page-in) { animation: 240ms $out 70ms both dm-rise-in; }

    /* Present / Dismiss: the wizard is a sheet over the machine room, not a page beside it. The
       sheet carries its own backdrop so nothing beneath shows through it while it moves. */
    ${kind(Motion.Present)}::view-transition-new(dm-page-in), ${kind(Motion.Dismiss)}::view-transition-old(dm-page-out) {
      background-color: ${DmColor.bg};
      background-image: ${DmEffects.gridBackdrop};
      background-size: ${DmEffects.gridBackdropSize};
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -16px 44px rgba(0,0,0,.6);
    }
    ${kind(Motion.Present)}::view-transition-group(dm-page-in) { z-index: 1; }
    ${kind(Motion.Present)}::view-transition-old(dm-page-out) { animation: $sheetIn $snap both dm-recede; }
    ${kind(Motion.Present)}::view-transition-new(dm-page-in) { animation: $sheetIn $snap both dm-sheet-in; }
    ${kind(Motion.Dismiss)}::view-transition-group(dm-page-out) { z-index: 1; }
    ${kind(Motion.Dismiss)}::view-transition-old(dm-page-out) { animation: $sheetOut $exit both dm-sheet-out; }
    ${kind(Motion.Dismiss)}::view-transition-new(dm-page-in) { animation: $sheetIn $snap both dm-emerge; }

    /* Morph: a row grows into the screen it opens, and the screen shrinks back into its row. The
       container is opaque so the screen underneath never shows through the one in motion. */
    html[data-dm-vt-morphed]::view-transition-group(${ViewTransitions.MORPH_NAME}) {
      animation-duration: 420ms;
      overflow: clip;
      border-radius: ${DmEffects.radiusMd};
      background-color: ${DmColor.bg};
    }
    html[data-dm-vt-morphed]::view-transition-old(${ViewTransitions.MORPH_NAME}),
    html[data-dm-vt-morphed]::view-transition-new(${ViewTransitions.MORPH_NAME}) {
      block-size: 100%;
      object-fit: none;
      object-position: left top;
      mix-blend-mode: normal;
    }
    ${kind(Motion.Forward)}[data-dm-vt-morphed]::view-transition-old(${ViewTransitions.MORPH_NAME}) { animation: 110ms linear both dm-fade-out; }
    ${kind(Motion.Forward)}[data-dm-vt-morphed]::view-transition-new(${ViewTransitions.MORPH_NAME}) { animation: 200ms $out 40ms both dm-fade-in; }
    ${kind(Motion.Forward)}[data-dm-vt-morphed]::view-transition-old(dm-page-out) { animation: 320ms $out both dm-settle-out; }
    ${kind(Motion.Back)}[data-dm-vt-morphed]::view-transition-old(${ViewTransitions.MORPH_NAME}) { animation: 200ms $out both dm-fade-out; }
    ${kind(Motion.Back)}[data-dm-vt-morphed]::view-transition-new(${ViewTransitions.MORPH_NAME}) { animation: 150ms $out 140ms both dm-fade-in; }
    ${kind(Motion.Back)}[data-dm-vt-morphed]::view-transition-new(dm-page-in) { animation: 260ms $out both dm-settle-in; }

    /* The wizard's stepper stays put between steps; its new state is shown live so the cogs'
       own colour transitions play. */
    ::view-transition-group(dm-stepper) { animation: none; z-index: 1; }
    ::view-transition-old(dm-stepper) { animation: none; opacity: 0; }
    ::view-transition-new(dm-stepper) { animation: none; }

    /* Update: the page holds still; rows that moved slide, new rows drop in, removed rows fold away. */
    ${kind(Motion.Update)}::view-transition-group(dm-page) { animation-duration: 260ms; }
    ${kind(Motion.Update)}::view-transition-old(dm-page) { animation: 120ms $exit both dm-fade-out; }
    ${kind(Motion.Update)}::view-transition-new(dm-page) { animation: 200ms $out 60ms both dm-fade-in; }
    ::view-transition-group(*.dm-item) { animation-duration: 340ms; }
    ::view-transition-new(*.dm-item):only-child { animation: 320ms $snap 70ms both dm-item-in; }
    ::view-transition-old(*.dm-item):only-child { animation: 160ms $exit both dm-item-out; }
    ::view-transition-old(*.dm-ticker):only-child { animation: 170ms $exit both dm-ticker-out; }
    ::view-transition-new(*.dm-ticker):only-child { animation: 360ms $snap 90ms both dm-ticker-in; }
    ::view-transition-old(*.dm-stamp):only-child { animation: 120ms $exit both dm-fade-out; }
    ::view-transition-new(*.dm-stamp):only-child { animation: 460ms $spring 60ms both dm-stamp-in; }
    ::view-transition-group(dm-segment) { animation-duration: 300ms; }
  """.trimIndent()
}
